import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { buildMenuCatalog } from "@/core/navigation/menu-catalog";
import {
  MENU_DEMO_PRESETS,
  clearHiddenMenuPaths,
  getHiddenMenuPaths,
  getMenuVisibilitySnapshot,
  setHiddenMenuPaths,
  subscribeMenuVisibility,
} from "@/core/navigation/menu-visibility";
import { routes } from "@/router/all_routes";
import { useAuth } from "@itaas/core/auth/useAuth";

export function AdminMenuVisibilityPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const visibilityKey = useSyncExternalStore(
    subscribeMenuVisibility,
    getMenuVisibilitySnapshot,
    () => "[]",
  );

  const catalog = useMemo(() => buildMenuCatalog(auth), [auth]);
  const allPaths = useMemo(
    () => catalog.flatMap((s) => s.items.map((i) => i.path)),
    [catalog],
  );

  const [hidden, setHidden] = useState<Set<string>>(() => getHiddenMenuPaths());
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  useEffect(() => {
    setHidden(getHiddenMenuPaths());
  }, [visibilityKey]);

  const isVisible = (path: string) => !hidden.has(path);

  const setPathVisible = (path: string, visible: boolean) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (visible) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const setSectionVisible = (sectionId: string, visible: boolean) => {
    const section = catalog.find((s) => s.id === sectionId);
    if (!section) return;
    setHidden((prev) => {
      const next = new Set(prev);
      for (const item of section.items) {
        if (visible) next.delete(item.path);
        else next.add(item.path);
      }
      return next;
    });
  };

  const sectionState = (sectionId: string): "all" | "none" | "some" => {
    const section = catalog.find((s) => s.id === sectionId);
    if (!section) return "none";
    const visibleCount = section.items.filter((i) => isVisible(i.path)).length;
    if (visibleCount === 0) return "none";
    if (visibleCount === section.items.length) return "all";
    return "some";
  };

  const applyHidden = (next: Set<string>) => {
    setHidden(next);
  };

  const save = () => {
    setHiddenMenuPaths([...hidden]);
    setSavedMsg("Menus mis à jour — la barre latérale a été rafraîchie.");
    window.setTimeout(() => setSavedMsg(null), 3500);
  };

  const visibleCount = allPaths.filter((p) => isVisible(p)).length;

  return (
    <div>
      <PageHeader
        title="Personnaliser les menus"
        description="Mode démo — choisissez les entrées visibles dans la barre latérale pour votre présentation."
        actions={
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate(routes.tenantDashboard)}
          >
            Tableau de bord
          </button>
        }
      />

      <div className="alert alert-info py-2 small mb-4">
        Cette page reste toujours accessible sous <strong>Administration</strong>, même si vous
        masquez d&apos;autres menus. Les réglages sont enregistrés dans ce navigateur uniquement.
      </div>

      {savedMsg && <div className="alert alert-success py-2">{savedMsg}</div>}

      <div className="ph-invoice-sheet mb-4">
        <div className="ph-invoice-section-title">Préréglages rapides</div>
        <div className="d-flex flex-wrap gap-2 ph-menu-preset-actions">
          {Object.values(MENU_DEMO_PRESETS).map((preset) => (
            <button
              key={preset.label}
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => applyHidden(new Set(preset.hidden))}
            >
              {preset.label}
            </button>
          ))}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => applyHidden(new Set())}
          >
            Tout cocher
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => applyHidden(new Set(allPaths))}
          >
            Tout décocher
          </button>
        </div>
        <p className="small text-muted mb-0 mt-2">
          {visibleCount} / {allPaths.length} entrées visibles (aperçu non enregistré tant que vous
          n&apos;avez pas cliqué Enregistrer).
        </p>
      </div>

      <div className="row g-3">
        {catalog.map((section) => {
          const state = sectionState(section.id);
          return (
            <div key={section.id} className="col-12 col-md-6 col-xl-4">
              <div className="ph-invoice-sheet h-100">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input mt-0"
                    id={`section-${section.id}`}
                    checked={state === "all"}
                    ref={(el) => {
                      if (el) el.indeterminate = state === "some";
                    }}
                    onChange={(e) => setSectionVisible(section.id, e.target.checked)}
                  />
                  <label htmlFor={`section-${section.id}`} className="fw-semibold mb-0">
                    {section.title}
                  </label>
                </div>
                <ul className="list-unstyled mb-0 ph-menu-toggles">
                  {section.items.map((item) => (
                    <li key={item.path} className="d-flex align-items-center gap-2 py-1">
                      <input
                        type="checkbox"
                        className="form-check-input mt-0"
                        id={`menu-${item.path}`}
                        checked={isVisible(item.path)}
                        onChange={(e) => setPathVisible(item.path, e.target.checked)}
                      />
                      <label htmlFor={`menu-${item.path}`} className="small mb-0 flex-grow-1">
                        {item.label}
                        {item.badge === "live" && (
                          <span className="badge bg-success-subtle text-success ms-1">Actif</span>
                        )}
                        {item.badge === "soon" && (
                          <span className="badge bg-secondary-subtle text-secondary ms-1">
                            Bientôt
                          </span>
                        )}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      <div className="d-flex flex-wrap gap-2 mt-4 sticky-bottom ph-menu-save-bar ph-page-header-actions">
        <button type="button" className="btn btn-primary" onClick={save}>
          Enregistrer et appliquer
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => {
            clearHiddenMenuPaths();
            setHidden(new Set());
            setSavedMsg("Tous les menus sont à nouveau visibles.");
            window.setTimeout(() => setSavedMsg(null), 3500);
          }}
        >
          Réinitialiser (tout afficher)
        </button>
      </div>
    </div>
  );
}
